import React from 'react';
import { Typography, Box } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import CheckCircle from '@material-ui/icons/CheckCircleOutlineOutlined';
import { ActiveStep } from '../OnboardingContext';
import { OnboardingMessageKey } from '../../../translations/messages';
import { OnboardingLanguage } from '../../../translations';
import { getOnboardingColors, OnboardingColors } from '../colors';
import { getJourney2Steps } from './journey2Steps';
import { getJourney3Steps } from './journey3Steps';
import { QuizStep } from './QuizStep';
import { journey1Quiz } from './quizQuestions';
import { OnboardingVideo } from './OnboardingVideo';
import { OnboardingImage } from './OnboardingImage';

/* ── helper: permission list item ── */
export const CanItem = ({ text }: { text: string }) => {
  const theme = useTheme();
  const c = getOnboardingColors(theme);
  return (
    <Box style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
      <CheckCircleOutlineIcon style={{ fontSize: 18, color: c.success }} />
      <Typography variant="body2">{text}</Typography>
    </Box>
  );
};

export const CannotItem = ({ text }: { text: string }) => {
  const theme = useTheme();
  const c = getOnboardingColors(theme);
  return (
    <Box style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
      <CancelOutlinedIcon style={{ fontSize: 18, color: c.error }} />
      <Typography variant="body2">{text}</Typography>
    </Box>
  );
};

/* ── helper: flow-diagram box ── */
export const FlowBox = ({
  number,
  title,
  subtitle,
}: {
  number: number;
  title: string;
  subtitle: string;
}) => {
  const theme = useTheme();
  const c = getOnboardingColors(theme);
  return (
    <Box
      style={{
        border: `2px solid ${c.flowAccent}`,
        borderRadius: 8,
        padding: '10px 14px',
        textAlign: 'center',
        minWidth: 110,
        flex: 1,
      }}
    >
      <Typography variant="body2" style={{ fontWeight: 700, marginBottom: 2 }}>
        {number}. {title}
      </Typography>
      <Typography variant="caption" style={{ color: c.flowSubtitle }}>
        {subtitle}
      </Typography>
    </Box>
  );
};

/* ── helper: arrow between flow boxes ── */
export const FlowArrow = () => {
  const theme = useTheme();
  const c = getOnboardingColors(theme);
  return (
    <ArrowForwardIcon
      style={{ color: c.flowAccent, fontSize: 22, margin: '0 4px', flexShrink: 0 }}
    />
  );
};

/** Translation function type matching Backstage's useTranslationRef t(). */
export type TFunction = (
  key: OnboardingMessageKey,
  values?: Record<string, string | number>,
) => string;

/* ================================================================
 *  Step metadata — shared across all languages
 * ================================================================ */

interface StepDef {
  sectionKey: OnboardingMessageKey;
  titleKey: OnboardingMessageKey;
  stepIndex: number;
  totalSteps: number;
  targetSelector?: string;
  placement?: 'left' | 'right' | 'top' | 'bottom';
  navigateTo?: string;
  customActions?: boolean;
  hideStepTitle?: boolean;
  centerContent?: boolean;
  stepType?: 'quiz' | 'playground';
}

const journey0StepDefs: StepDef[] = [
  /* Phase 1 — Welcome & Platform Overview (3 steps) */
  { sectionKey: 'sectionWelcomePlatform', titleKey: 'stepWelcomeIdp', stepIndex: 1, totalSteps: 3 },
  { sectionKey: 'sectionWelcomePlatform', titleKey: 'stepToolsAroundYou', stepIndex: 2, totalSteps: 3 },
  { sectionKey: 'sectionWelcomePlatform', titleKey: 'stepHowItConnects', stepIndex: 3, totalSteps: 3 },
  /* Phase 2 — Navigating Backstage (6 steps) */
  { sectionKey: 'sectionNavigatingBackstage', titleKey: 'stepHomePage', stepIndex: 1, totalSteps: 6 },
  { sectionKey: 'sectionNavigatingBackstage', titleKey: 'stepQuickActions', stepIndex: 2, totalSteps: 6, targetSelector: '[data-onboarding="quick-actions"]', placement: 'left' },
  { sectionKey: 'sectionNavigatingBackstage', titleKey: 'stepSearchBar', stepIndex: 3, totalSteps: 6, targetSelector: '[data-onboarding="search-bar"]', placement: 'bottom' },
  { sectionKey: 'sectionNavigatingBackstage', titleKey: 'stepOpenCatalog', stepIndex: 4, totalSteps: 6, targetSelector: '[data-onboarding="sidebar-catalog"]', placement: 'right' },
  { sectionKey: 'sectionNavigatingBackstage', titleKey: 'stepUnderstandingKinds', stepIndex: 5, totalSteps: 6, navigateTo: '/catalog', targetSelector: '[data-onboarding="catalog-kind-filter"]', placement: 'right' },
  { sectionKey: 'sectionNavigatingBackstage', titleKey: 'stepFiltersSearch', stepIndex: 6, totalSteps: 6, navigateTo: '/catalog', targetSelector: '[data-onboarding="catalog-filters"]', placement: 'right' },
  /* Phase 3 — Finding Your Project (3 steps) */
  { sectionKey: 'sectionFindingProject', titleKey: 'stepFindSystem', stepIndex: 1, totalSteps: 3, navigateTo: '/catalog', targetSelector: '[data-onboarding="catalog-table"]', placement: 'left' },
  { sectionKey: 'sectionFindingProject', titleKey: 'stepSystemOverview', stepIndex: 2, totalSteps: 3 },
  { sectionKey: 'sectionFindingProject', titleKey: 'stepRelationsGraph', stepIndex: 3, totalSteps: 3 },
  /* Phase 4 — External Tools Overview (3 steps) */
  { sectionKey: 'sectionExternalTools', titleKey: 'stepArgoCd', stepIndex: 1, totalSteps: 3 },
  { sectionKey: 'sectionExternalTools', titleKey: 'stepGrafana', stepIndex: 2, totalSteps: 3 },
  { sectionKey: 'sectionExternalTools', titleKey: 'stepGitHubAzureDevOps', stepIndex: 3, totalSteps: 3 },
  /* Phase 5 — Permissions & Security Rules (3 steps) */
  { sectionKey: 'sectionPermissionsSecurity', titleKey: 'stepPermissionsDeveloper', stepIndex: 1, totalSteps: 3 },
  { sectionKey: 'sectionPermissionsSecurity', titleKey: 'stepSecurityRules', stepIndex: 2, totalSteps: 3 },
  { sectionKey: 'sectionPermissionsSecurity', titleKey: 'stepJourneyComplete', stepIndex: 3, totalSteps: 3, hideStepTitle: true, centerContent: true },
  /* Phase 6 — Quiz (1 step, custom actions) */
  { sectionKey: 'sectionQuiz', titleKey: 'stepQuiz', stepIndex: 1, totalSteps: 1, customActions: true, stepType: 'quiz' },
];

/* ================================================================
 *  Content per language
 * ================================================================ */

type ContentGetter = (c: OnboardingColors) => React.ReactNode[];

const journey0ContentByLanguage: Record<OnboardingLanguage, ContentGetter> = {
  en: (c) => getJourney0ContentEN(c),
  es: (c) => getJourney0ContentES(c),
  'pt-BR': (c) => getJourney0ContentPtBR(c),
};

/* ================================================================
 *  Public API
 * ================================================================ */

/**
 * Returns the spotlight steps for a given journey.
 *
 * Accepts a translation function `t` and `language` so all visible
 * strings are localised.  When Backstage ships its i18n layer the `t`
 * parameter will come from `useTranslationRef()` instead.
 */
export const getJourneySteps = (
  journeyIndex: number,
  journeyTitle: string,
  t: TFunction = key => key,
  language: OnboardingLanguage = 'en',
  colors?: OnboardingColors,
): ActiveStep[] => {
  const journeyLabel = `${t('journeyLabel', { index: journeyIndex + 1 })}: ${journeyTitle}`;

  switch (journeyIndex) {
    case 0: {
      const getContent = journey0ContentByLanguage[language] ?? journey0ContentByLanguage.en;
      const fallbackColors: OnboardingColors = colors ?? ({} as OnboardingColors);
      const contents = getContent(fallbackColors);

      // Build quiz content for the last step
      const questions = journey1Quiz[language] ?? journey1Quiz.en;
      const quizContent = (
        <QuizStep
          questions={questions}
          minCorrect={9}
          labels={{
            questionOf: t('quizQuestionOf'),
            back: t('back'),
            confirm: t('quizConfirm'),
            continueLabel: t('continueLabel'),
            correctAnswer: t('quizCorrectAnswer'),
            quizPassed: t('quizPassed'),
            quizFailed: t('quizFailed'),
            correctAnswers: t('quizCorrectAnswers'),
            nextJourneyUnlocked: t('quizNextJourneyUnlocked'),
            continueToJourney: t('quizContinueToJourney'),
            tryAgain: t('quizTryAgain'),
            failedMessage: t('quizFailedMessage'),
            reviewJourney: t('quizReviewJourney'),
          }}
        />
      );
      const allContents = [...contents, quizContent];

      return journey0StepDefs.map((def, i) => ({
        journeyIndex,
        journeyTitle: journeyLabel,
        sectionTitle: t(def.sectionKey),
        stepIndex: def.stepIndex,
        totalSteps: def.totalSteps,
        stepTitle: t(def.titleKey),
        stepContent: allContents[i],
        ...(def.targetSelector && { targetSelector: def.targetSelector }),
        ...(def.placement && { placement: def.placement }),
        ...(def.navigateTo && { navigateTo: def.navigateTo }),
        ...(def.customActions && { customActions: def.customActions }),
        ...(def.stepType && { stepType: def.stepType }),
        ...(def.hideStepTitle && { hideStepTitle: def.hideStepTitle }),
        ...(def.centerContent && { centerContent: def.centerContent }),
      }));
    }
    case 1: {
      return getJourney2Steps(journeyIndex, journeyTitle, t, language, colors);
    }
    case 2: {
      return getJourney3Steps(journeyIndex, journeyTitle, t, language, colors);
    }
    default:
      return [];
  }
};

/* ================================================================
 *  Journey 0 — English content
 * ================================================================ */

const getJourney0ContentEN = (c: OnboardingColors): React.ReactNode[] => [
  /* 0 — Welcome to the IDP */
  <Box key="en-0">
    <Typography variant="body2" paragraph>
      The IDP is Nestlé's centralized developer portal, built on Backstage.
      Instead of jumping between tools, you get one entry point for your
      services, infrastructure, docs, pipelines, and monitoring.
    </Typography>
    <Typography variant="body2" component="div">
      <strong>What you need to know</strong>
      <ul>
        <li>Your project is organized as a System, grouping your apps (Components) and infrastructure (Resources) together.</li>
        <li>Code lives in GitHub. Deploys go to Kubernetes via ArgoCD, one cluster per environment.</li>
        <li>Each deploy requires GitOps files as the source of truth, in a unified repo on GitHub.</li>
        <li>Resources run on Azure Cloud. Logs are in Grafana, one instance per environment.</li>
        <li>Everything is cataloged in Backstage — services, resources, DNS, and docs.</li>
      </ul>
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>What you'll learn in this journey</strong>
    </Typography>
    <Typography variant="body2" paragraph>
      How to navigate Backstage, find your System, use ArgoCD, Grafana, GitHub
      and Azure DevOps, and understand your permissions and security rules.
    </Typography>
  </Box>,

  /* 1 — The tools around you */
  <Box key="en-1">
    <Typography variant="body2" paragraph>
      The platform integrates several tools, each handling a specific part of
      your workflow. Backstage is your central hub — you start here and
      navigate to the others as needed.
    </Typography>
    <OnboardingVideo videoPath="Journey%201/Step2/Video_Onboarding_Journey1_Step2_EN.mp4" subtitlePath="Journey%201/Step2/Legendas/step02_tools_around_you_EN.srt" />
    <Typography variant="body2" component="div">
      <strong>Your daily toolkit</strong>
      <ul>
        <li><strong>Backstage:</strong> Developer Portal. Browse the catalog, find components and resources, access docs and external tools.</li>
        <li><strong>GitHub:</strong> Source code and GitOps repositories. Push code and create pull requests here.</li>
        <li><strong>Azure DevOps:</strong> CI/CD pipelines. Automatically runs tests, builds images, and updates GitOps on every push.</li>
        <li><strong>ArgoCD:</strong> Deployments. Watches GitOps and applies changes to Kubernetes automatically.</li>
        <li><strong>Grafana + Loki:</strong> Logs and monitoring. Query application logs across environments via Explore.</li>
      </ul>
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText }}>
      Crossplane and Kubernetes work behind the scenes, you don't interact with them directly.
    </Typography>
  </Box>,

  /* 2 — How it all connects */
  <Box key="en-2">
    <OnboardingImage imagePath="images/Journey%201/Phase%201%20-%20Step%203/Frame.png" alt="How it all connects" height={180} />
    <Typography variant="body2" paragraph>
      Here's the end-to-end flow every time you push code:
    </Typography>
    <Typography variant="body2" component="div">
      <ol>
        <li>You push code to GitHub. A pull request triggers the CI pipeline.</li>
        <li>The pipeline runs three stages: Tests (unit + SonarQube) → Build and Push Image (Docker → ACR) → Update version in GitOps (bom.yaml).</li>
        <li>ArgoCD detects the GitOps change and automatically deploys to Kubernetes.</li>
        <li>You check deploy status in ArgoCD and application logs in Grafana.</li>
      </ol>
    </Typography>
    <Box style={{ backgroundColor: c.warningBannerBg, border: `1px solid ${c.warningBannerBg}`, borderRadius: 4, padding: '12px 16px', marginTop: 8 }}>
      <Typography variant="body2">
        The Dockerfile is NOT your responsibility, the Platform Team creates and maintains it. Even if you create one, it won't be used.
      </Typography>
    </Box>
  </Box>,

  /* 3 — The home page */
  <Box key="en-3">
    <Typography variant="body2" paragraph>
      The Home Page is your starting point every time you open Backstage. It
      adapts to your usage and gives you quick access to what matters most:
    </Typography>
    <Typography variant="body2" component="div">
      <ul>
        <li><strong>Top Visited:</strong> items you access most frequently</li>
        <li><strong>Recently Visited:</strong> chronological history of what you've looked at</li>
        <li><strong>Starred Entities:</strong> items you've manually marked as favorites</li>
        <li><strong>Featured Docs:</strong> documentation published through TechDocs</li>
      </ul>
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText }}>
      Tip: Star your project's System page early on, it will appear in Starred Entities for one-click access.
    </Typography>
  </Box>,

  /* 4 — Quick actions */
  <Box key="en-4">
    <Typography variant="body2" paragraph>
      These buttons are shortcuts to the most common tasks in Backstage:
    </Typography>
    <Typography variant="body2" component="div">
      <ul>
        <li><strong>CREATE COMPONENT:</strong> Visible to everyone, but clicking it as a Developer will show an error page. This is expected, not a bug.</li>
        <li><strong>BROWSE CATALOG:</strong> Opens the Catalog page where you explore Components, Resources, and Systems.</li>
        <li><strong>VIEW DOCS:</strong> Opens TechDocs with platform guides and team-published reference material.</li>
      </ul>
    </Typography>
    <Box style={{ backgroundColor: c.warningBannerBg, border: `1px solid ${c.warningBannerBg}`, borderRadius: 4, padding: '12px 16px', marginTop: 8 }}>
      <Typography variant="body2">
        Developers have read-only access. Only Tech Leads can create components and resources. If you need something created, ask your Tech Lead.
      </Typography>
    </Box>
  </Box>,

  /* 5 — The search bar */
  <Box key="en-5">
    <Typography variant="body2" paragraph>
      The search bar at the top of every page is your fastest way to find
      anything in the portal. It searches across all entities — Components,
      Resources, Systems, APIs, and documentation — indexing all fields: name,
      type, owner, and description.
    </Typography>
    <Typography variant="body2" paragraph>
      Results appear as you type. Click any result to go directly to its detail page.
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText }}>
      Use Search when you know what you're looking for. Use the Catalog when you want to browse by category with filters.
    </Typography>
  </Box>,

  /* 6 — Open the catalog */
  <Box key="en-6">
    <Typography variant="body2" paragraph>
      The Catalog is the heart of Backstage. All entities in the platform are
      listed and organized here: every Component, Resource, System, API, and
      more. Think of it as the master directory for everything your team works with.
    </Typography>
    <Typography variant="body2" paragraph>
      You can reach it by clicking "Catalog" in the left sidebar or the "BROWSE CATALOG" button on the Home Page.
    </Typography>
    <Typography variant="body2">
      It opens showing Components by default. The left sidebar has filters (Owner, Lifecycle) and the top right has a keyword search field.
    </Typography>
  </Box>,

  /* 7 — Understanding kinds */
  <Box key="en-7">
    <OnboardingVideo videoPath="Journey%201/Step8/Video_Onboarding_Journey1_Step8_EN.mp4" subtitlePath="Journey%201/Step8/Legenda/step08_understanding_kinds_EN.srt" />
    <Typography variant="body2" paragraph>
      Kinds are how the Catalog categorizes everything. There are three you'll use daily:
    </Typography>
    <Typography variant="body2" component="div">
      <ul>
        <li><strong>Component:</strong> Your applications: microservices, webapps, libraries, frontends. Each one has Plugin Tabs for Azure PRs, CI/CD, Grafana, and SonarQube.</li>
        <li><strong>Resource:</strong> Infrastructure items: Storage Accounts, KeyVaults, Redis, CosmosDB, EventHubs, Grafana Dashboards and Datasources. Filterable by type.</li>
        <li><strong>System:</strong> Your project. Groups all Components and Resources together, with a dependency graph and relations view.</li>
      </ul>
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText }}>
      Switch the Kind dropdown in the left sidebar to see different entity types.
    </Typography>
  </Box>,

  /* 8 — Filters & search */
  <Box key="en-8">
    <Typography variant="body2" paragraph>
      The Catalog gives you two ways to narrow down what you're looking at:
    </Typography>
    <Typography variant="body2" component="div">
      <ul>
        <li><strong>Sidebar filters:</strong> filter by Owner, Lifecycle, and Personal items (Owned or Starred).</li>
        <li><strong>Keyword search:</strong> the field in the top right filters the current view across all visible columns (name, owner, type, description).</li>
      </ul>
    </Typography>
    <Typography variant="body2" paragraph>
      Example: to find a specific KeyVault, set Kind to "Resource", then type the name in the search field.
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText }}>
      Note: this search only filters the current Kind view. The global Search bar at the top of the page searches everything in Backstage.
    </Typography>
  </Box>,

  /* 9 — Find your system */
  <Box key="en-9">
    <Typography variant="body2" paragraph>
      Your project is organized as a System: the top-level grouping that holds
      all your team's microservices (Components) and infrastructure (Resources)
      under one roof.
    </Typography>
    <Typography variant="body2" paragraph>
      To find it: go to the Catalog, set Kind to "System", locate your project
      by name or use the search field, and click to open.
    </Typography>
    <Typography variant="body2" paragraph>
      The System page is your project's home base in the IDP. From here you can
      see everything that belongs to your project, understand how the pieces
      connect, and navigate to any specific Component or Resource.
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText }}>
      Tip: star your System for one-click access from the Home Page.
    </Typography>
  </Box>,

  /* 10 — System overview */
  <Box key="en-10">
    <OnboardingImage imagePath="images/Journey%201/Phase%203%20-%20Step%202/Frame.png" alt="System overview" />
    <Typography variant="body2" paragraph>
      This is your project's home base. The About card on the left shows key metadata:
    </Typography>
    <Typography variant="body2" component="div">
      <ul>
        <li><strong>Description:</strong> what this system does</li>
        <li><strong>Owner:</strong> which team is responsible</li>
        <li><strong>Domain:</strong> the business area it belongs to</li>
        <li><strong>View Source / View TechDocs:</strong> quick links to the GitHub repository and published documentation</li>
      </ul>
    </Typography>
    <Typography variant="body2">
      Below, you'll find lists of all Components (your apps) and Resources
      (infrastructure) belonging to this System. Click any name to navigate to
      its detail page with specific information and Plugin Tabs.
    </Typography>
  </Box>,

  /* 11 — Relations graph */
  <Box key="en-11">
    <OnboardingImage imagePath="images/Journey%201/Phase%203%20-%20Step%203/Frame.png" alt="Relations graph" />
    <Typography variant="body2" paragraph>
      On the right side of the System page, the Relations card shows a preview
      of how everything in your project connects: which Components depend on
      which Resources, and how services relate to each other.
    </Typography>
    <Typography variant="body2" paragraph>
      Click "View graph" at the bottom for a full-page interactive view where
      you can zoom, pan, click individual nodes, and apply filters to focus on
      specific parts.
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText }}>
      Especially useful when you're new to a project, troubleshooting
      multi-service issues, or assessing the impact of a change.
    </Typography>
  </Box>,

  /* 12 — ArgoCD: your deploy dashboard */
  <Box key="en-12">
    <OnboardingVideo videoPath="Journey%201/Step13/Video_Onboarding_Journey1_Step13_EN.mp4" subtitlePath="Journey%201/Step13/Legenda/step13_argocd_EN.srt" />
    <Typography variant="body2" paragraph>
      ArgoCD handles all deployments. It watches your GitOps repository and
      automatically applies changes to the Kubernetes cluster. You'll use it to
      check if deploys were successful and monitor application health.
    </Typography>
    <Box style={{ backgroundColor: c.infoBoxBg, border: `1px solid ${c.infoBoxBorder}`, borderRadius: 4, padding: '10px 16px', textAlign: 'center', marginBottom: 12 }}>
      <Typography variant="body2">
        Access:{' '}
        <a href="https://argocd-nlar.vsa.nestle.biz" target="_blank" rel="noopener noreferrer" style={{ color: c.primary }}>
          argocd-nlar.vsa.nestle.biz
        </a>
      </Typography>
    </Box>
    <Typography variant="body2" paragraph>Use Label Filters to find your apps:</Typography>
    <Typography variant="body2" component="div">
      <ul>
        <li><strong>env=dv</strong> for <strong>Development</strong></li>
        <li><strong>env=ts</strong> for <strong>Test</strong></li>
        <li><strong>env=pr</strong> for <strong>Production</strong></li>
      </ul>
    </Typography>
    <Typography variant="body2" paragraph>You can also filter by domain or system name.</Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText }}>
      All apps have Auto Sync, Auto Prune, and Self Heal enabled. If you see nothing when you open ArgoCD, request access via ticket.
    </Typography>
  </Box>,

  /* 13 — Grafana: your logs */
  <Box key="en-13">
    <OnboardingVideo videoPath="Journey%201/Step14/Video_Onboarding_Journey1_Step14_EN.mp4" subtitlePath="Journey%201/Step14/Legenda/step14_grafana_EN.srt" />
    <Typography variant="body2" paragraph>
      Application logs are aggregated via Grafana Loki and available through Grafana. Each environment has its own dedicated instance:
    </Typography>
    <Box style={{ backgroundColor: c.infoBoxBg, border: `1px solid ${c.infoBoxBorder}`, borderRadius: 4, padding: '10px 16px', textAlign: 'center', marginBottom: 12 }}>
      <Typography variant="body2">
        Development:{' '}
        <a href="https://grafana-dv.vsa.nestle.biz" target="_blank" rel="noopener noreferrer" style={{ color: c.primary }}>grafana-dv.vsa.nestle.biz</a>
      </Typography>
      <Typography variant="body2">
        Test/Homolog:{' '}
        <a href="https://grafana-ts.vsa.nestle.biz" target="_blank" rel="noopener noreferrer" style={{ color: c.primary }}>grafana-ts.vsa.nestle.biz</a>
      </Typography>
      <Typography variant="body2">
        Production:{' '}
        <a href="https://grafana.vsa.nestle.biz" target="_blank" rel="noopener noreferrer" style={{ color: c.primary }}>grafana.vsa.nestle.biz</a>
      </Typography>
    </Box>
    <Typography variant="body2" paragraph>How to query logs</Typography>
    <Typography variant="body2" component="div">
      <ul>
        <li><strong>1.</strong> Go to Explore in the left sidebar</li>
        <li><strong>2.</strong> Select the Loki datasource (top left)</li>
        <li><strong>3.</strong> Choose Builder (visual) or Code mode</li>
        <li><strong>4.</strong> Click Run Query</li>
      </ul>
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText, marginBottom: 8 }}>
      Each team only has access to their own Organization. Check the top-left corner to see or switch Organizations.
    </Typography>
  </Box>,

  /* 14 — GitHub & Azure DevOps */
  <Box key="en-14">
    <OnboardingVideo videoPath="Journey%201/Step15/Video_Onboarding_Journey1_Step15_EN.mp4" subtitlePath="Journey%201/Step15/Legenda/step15_github_cicd_EN.srt" />
    <Typography variant="body2" style={{ fontWeight: 700, marginBottom: 8 }}>GitHub</Typography>
    <Typography variant="body2" paragraph>
      Each project has two repositories: the Application repo (your source code) and the GitOps repo (deployment configuration, with folders for each environment: dv, ts, pr). You push code to the Application repo. The GitOps repo is updated automatically by the pipeline. Never create branches in GitOps repos, they must use only main.
    </Typography>
    <Typography variant="body2" style={{ fontWeight: 700, marginBottom: 8 }}>Azure DevOps</Typography>
    <Typography variant="body2" paragraph>
      Runs your CI/CD pipelines. Every pipeline has 3 stages:
    </Typography>
    <Typography variant="body2" component="div">
      <ul>
        <li><strong>1. Tests:</strong> unit tests + SonarQube code quality analysis</li>
        <li><strong>2. Build and Push Image:</strong> Docker image to Azure Container Registry</li>
        <li><strong>3. Update version in GitOps:</strong> updates bom.yaml with the new image version</li>
      </ul>
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText, marginBottom: 8 }}>
      All pipelines are pre-configured by the Platform Team. Each team has its own Azure DevOps workspace.
    </Typography>
    <Box style={{ backgroundColor: c.warningBannerBg, border: `1px solid ${c.warningBoxBorder}`, borderRadius: 4, padding: '12px 16px', marginTop: 8 }}>
      <Typography variant="body2">
        Dockerfiles are created and maintained exclusively by the Platform Team. Even if you create your own, the pipeline will not use it.
      </Typography>
    </Box>
  </Box>,

  /* 15 — Your permissions as developer */
  <Box key="en-15">
    <Typography variant="body2" paragraph>
      The IDP separates what Developers and Tech Leads can do. Understanding your permissions avoids confusion and saves time.
    </Typography>
    <Box style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
      <Box style={{ flex: 1, border: `2px solid ${c.success}`, borderRadius: 8, padding: '12px 14px' }}>
        <Typography variant="body2" style={{ fontWeight: 700, color: c.success, marginBottom: 8 }}>You CAN</Typography>
        <CanItem text="Browse the entire Catalog" />
        <CanItem text="Access ArgoCD for deploy status" />
        <CanItem text="Access Grafana for logs" />
        <CanItem text="Access Azure DevOps for pipelines" />
        <CanItem text="Access GitHub to push code" />
        <CanItem text="View Plugin Tabs on Components" />
      </Box>
      <Box style={{ flex: 1, border: `2px solid ${c.error}`, borderRadius: 8, padding: '12px 14px' }}>
        <Typography variant="body2" style={{ fontWeight: 700, color: c.error, marginBottom: 8 }}>You CANNOT</Typography>
        <CannotItem text="Create or edit Resources" />
        <CannotItem text="Create apps from templates" />
        <CannotItem text="Promote environments" />
        <CannotItem text="Manage secrets in KeyVault" />
        <CannotItem text="Generate SAS tokens" />
        <CannotItem text="Manage App Configurations" />
      </Box>
    </Box>
    <Typography variant="body2">
      Need something created or changed? Contact your Tech Lead. They have full create and edit permissions.
    </Typography>
  </Box>,

  /* 16 — Security rules (must know) */
  <Box key="en-16">
    <Typography variant="body2" paragraph>
      These rules are mandatory. Non-compliance triggers automated alerts and may result in access revocation.
    </Typography>
    <Box style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
      <Box style={{ flex: 1, border: `2px solid ${c.error}`, borderRadius: 8, padding: '12px 14px' }}>
        <Typography variant="body2" style={{ fontWeight: 700, color: c.error, marginBottom: 8 }}>Prohibited</Typography>
        <CannotItem text="Access to Azure Portal" />
        <CannotItem text="Hardcoded secrets in code" />
        <CannotItem text="Code outside Azure DevOps/GitHub" />
        <CannotItem text="External cloud resources" />
        <CannotItem text="Public ChatGPT with Nestlé data" />
      </Box>
      <Box style={{ flex: 1, border: `2px solid ${c.success}`, borderRadius: 8, padding: '12px 14px' }}>
        <Typography variant="body2" style={{ fontWeight: 700, color: c.success, marginBottom: 8 }}>Required</Typography>
        <CanItem text="VPN or Citrix always" />
        <CanItem text="Test coverage above 95%" />
        <CanItem text="Peer code review for all PRs" />
        <CanItem text="CI pipeline always passing" />
        <CanItem text="Files only in OneDrive/Confluence/Wiki" />
      </Box>
    </Box>
    <Typography variant="body2">
      These rules protect Nestlé's data, ensure compliance with global security policies, and maintain platform integrity. They apply to all team members regardless of role.
    </Typography>
  </Box>,

  /* 17 — Journey 1 complete! */
  <Box key="en-17" style={{ textAlign: 'center' }}>
    <CheckCircle style={{ fontSize: 56, color: c.success, marginBottom: 8 }} />
    <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 4 }}>
      Journey 1 complete!
    </Typography>
    <Typography variant="body2" style={{ color: c.subtitleText, marginBottom: 16 }}>
      Congratulations! You now have the foundation to move confidently through the platform.
    </Typography>
    <Box style={{ textAlign: 'left', marginBottom: 16 }}>
      <Typography variant="body2" style={{ fontWeight: 700, marginBottom: 8 }}>What you've learned</Typography>
      <CanItem text="How to navigate the Backstage portal: Home, Search, Catalog" />
      <CanItem text="How to find your project: Systems, Components, Resources, Relations Graph" />
      <CanItem text="The tools you'll use daily: ArgoCD, Grafana, GitHub, Azure DevOps" />
      <CanItem text="Your permissions as a Developer and the mandatory security rules" />
    </Box>
    <Box style={{ textAlign: 'left' }}>
      <Typography variant="body2" style={{ fontWeight: 700, marginBottom: 8 }}>Next step</Typography>
      <Typography variant="body2">
        Complete the quiz to validate your knowledge. You need at least 90% (9 out of 10 correct) to pass. If you don't pass on the first try, you can retake it. Once you pass, Journey 2: "Deploy: From Code to Live" will be unlocked.
      </Typography>
    </Box>
  </Box>,
];

/* ================================================================
 *  Journey 0 — Portuguese (pt-BR) content
 * ================================================================ */

const getJourney0ContentPtBR = (c: OnboardingColors): React.ReactNode[] => [
  /* 0 — Bem-vindo ao IDP */
  <Box key="pt-0">
    <Typography variant="body2" paragraph>
      O IDP é o portal centralizado de desenvolvedores da Nestlé, construído
      com Backstage. Em vez de pular entre ferramentas, você tem um único ponto
      de entrada para seus serviços, infraestrutura, documentação, pipelines e
      monitoramento.
    </Typography>
    <Typography variant="body2" component="div">
      <strong>O que você precisa saber</strong>
      <ul>
        <li>Seu projeto é organizado como um System, agrupando suas aplicações (Components) e infraestrutura (Resources).</li>
        <li>O código fica no GitHub. Deploys vão para o Kubernetes via ArgoCD, um cluster por ambiente.</li>
        <li>Cada deploy requer arquivos GitOps como fonte da verdade, em um repositório unificado no GitHub.</li>
        <li>Recursos rodam na Azure Cloud. Logs ficam no Grafana, uma instância por ambiente.</li>
        <li>Tudo é catalogado no Backstage — serviços, recursos, DNS e documentação.</li>
      </ul>
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>O que você vai aprender nesta jornada</strong>
    </Typography>
    <Typography variant="body2" paragraph>
      Como navegar no Backstage, encontrar seu System, usar ArgoCD, Grafana,
      GitHub e Azure DevOps, e entender suas permissões e regras de segurança.
    </Typography>
  </Box>,

  /* 1 — As ferramentas ao seu redor */
  <Box key="pt-1">
    <Typography variant="body2" paragraph>
      A plataforma integra várias ferramentas, cada uma cuidando de uma parte
      específica do seu fluxo de trabalho. O Backstage é seu hub central — você
      começa aqui e navega para as outras conforme necessário.
    </Typography>
    <OnboardingVideo videoPath="Journey%201/Step2/Video_Onboarding_Journey1_Step2_PT.mp4" subtitlePath="Journey%201/Step2/Legendas/step02_tools_around_you_PT.srt" />
    <Typography variant="body2" component="div">
      <strong>Seu kit de ferramentas diário</strong>
      <ul>
        <li><strong>Backstage:</strong> Portal do Desenvolvedor. Navegue no catálogo, encontre componentes e recursos, acesse documentação e ferramentas externas.</li>
        <li><strong>GitHub:</strong> Repositórios de código-fonte e GitOps. Envie código e crie pull requests aqui.</li>
        <li><strong>Azure DevOps:</strong> Pipelines CI/CD. Executa testes automaticamente, constrói imagens e atualiza o GitOps a cada push.</li>
        <li><strong>ArgoCD:</strong> Deployments. Monitora o GitOps e aplica mudanças no Kubernetes automaticamente.</li>
        <li><strong>Grafana + Loki:</strong> Logs e monitoramento. Consulte logs das aplicações em todos os ambientes via Explore.</li>
      </ul>
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText }}>
      Crossplane e Kubernetes trabalham nos bastidores, você não interage com eles diretamente.
    </Typography>
  </Box>,

  /* 2 — Como tudo se conecta */
  <Box key="pt-2">
    <OnboardingImage imagePath="images/Journey%201/Phase%201%20-%20Step%203/Frame.png" alt="Como tudo se conecta" height={180} />
    <Typography variant="body2" paragraph>
      Aqui está o fluxo completo toda vez que você envia código:
    </Typography>
    <Typography variant="body2" component="div">
      <ol>
        <li>Você envia código para o GitHub. Um pull request aciona a pipeline CI.</li>
        <li>A pipeline executa três estágios: Testes (unitários + SonarQube) → Build e Push de Imagem (Docker → ACR) → Atualizar versão no GitOps (bom.yaml).</li>
        <li>O ArgoCD detecta a mudança no GitOps e faz o deploy automaticamente no Kubernetes.</li>
        <li>Você verifica o status do deploy no ArgoCD e os logs da aplicação no Grafana.</li>
      </ol>
    </Typography>
    <Box style={{ backgroundColor: c.warningBannerBg, border: `1px solid ${c.warningBoxBorder}`, borderRadius: 4, padding: '12px 16px', marginTop: 8 }}>
      <Typography variant="body2">
        O Dockerfile NÃO é sua responsabilidade, o Time de Plataforma cria e mantém. Mesmo se você criar um, ele não será usado.
      </Typography>
    </Box>
  </Box>,

  /* 3 — A página inicial */
  <Box key="pt-3">
    <Typography variant="body2" paragraph>
      A Página Inicial é seu ponto de partida toda vez que você abre o
      Backstage. Ela se adapta ao seu uso e dá acesso rápido ao que mais
      importa:
    </Typography>
    <Typography variant="body2" component="div">
      <ul>
        <li><strong>Mais Visitados:</strong> itens que você acessa com mais frequência</li>
        <li><strong>Visitados Recentemente:</strong> histórico cronológico do que você viu</li>
        <li><strong>Entidades Favoritas:</strong> itens que você marcou manualmente como favoritos</li>
        <li><strong>Documentação em Destaque:</strong> documentação publicada pelo TechDocs</li>
      </ul>
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText }}>
      Dica: Favorite a página do System do seu projeto logo no início, ela aparecerá em Entidades Favoritas para acesso com um clique.
    </Typography>
  </Box>,

  /* 4 — Ações rápidas */
  <Box key="pt-4">
    <Typography variant="body2" paragraph>
      Esses botões são atalhos para as tarefas mais comuns no Backstage:
    </Typography>
    <Typography variant="body2" component="div">
      <ul>
        <li><strong>CREATE COMPONENT:</strong> Visível para todos, mas clicar como Desenvolvedor mostrará uma página de erro. Isso é esperado, não é um bug.</li>
        <li><strong>BROWSE CATALOG:</strong> Abre a página do Catálogo onde você explora Components, Resources e Systems.</li>
        <li><strong>VIEW DOCS:</strong> Abre o TechDocs com guias da plataforma e material de referência publicado pelo time.</li>
      </ul>
    </Typography>
    <Box style={{ backgroundColor: c.warningBannerBg, border: `1px solid ${c.warningBoxBorder}`, borderRadius: 4, padding: '12px 16px', marginTop: 8 }}>
      <Typography variant="body2">
        Desenvolvedores têm acesso somente leitura. Apenas Tech Leads podem criar componentes e recursos. Se precisar de algo criado, peça ao seu Tech Lead.
      </Typography>
    </Box>
  </Box>,

  /* 5 — A barra de busca */
  <Box key="pt-5">
    <Typography variant="body2" paragraph>
      A barra de busca no topo de cada página é a forma mais rápida de encontrar
      qualquer coisa no portal. Ela busca em todas as entidades — Components,
      Resources, Systems, APIs e documentação — indexando todos os campos: nome,
      tipo, proprietário e descrição.
    </Typography>
    <Typography variant="body2" paragraph>
      Os resultados aparecem enquanto você digita. Clique em qualquer resultado para ir direto à sua página de detalhes.
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText }}>
      Use a Busca quando souber o que está procurando. Use o Catálogo quando quiser navegar por categoria com filtros.
    </Typography>
  </Box>,

  /* 6 — Abrir o catálogo */
  <Box key="pt-6">
    <Typography variant="body2" paragraph>
      O Catálogo é o coração do Backstage. Todas as entidades da plataforma
      estão listadas e organizadas aqui: cada Component, Resource, System, API
      e mais. Pense nele como o diretório mestre de tudo com que seu time trabalha.
    </Typography>
    <Typography variant="body2" paragraph>
      Você pode acessá-lo clicando em "Catalog" na barra lateral esquerda ou no botão "BROWSE CATALOG" na Página Inicial.
    </Typography>
    <Typography variant="body2">
      Ele abre mostrando Components por padrão. A barra lateral esquerda tem filtros (Owner, Lifecycle) e no canto superior direito há um campo de busca por palavra-chave.
    </Typography>
  </Box>,

  /* 7 — Entendendo os tipos */
  <Box key="pt-7">
    <OnboardingVideo videoPath="Journey%201/Step8/Video_Onboarding_Journey1_Step8_PT.mp4" subtitlePath="Journey%201/Step8/Legenda/step08_understanding_kinds_PT.srt" />
    <Typography variant="body2" paragraph>
      Kinds (tipos) são como o Catálogo categoriza tudo. Existem três que você usará diariamente:
    </Typography>
    <Typography variant="body2" component="div">
      <ul>
        <li><strong>Component:</strong> Suas aplicações: microsserviços, webapps, bibliotecas, frontends. Cada um tem Plugin Tabs para Azure PRs, CI/CD, Grafana e SonarQube.</li>
        <li><strong>Resource:</strong> Itens de infraestrutura: Storage Accounts, KeyVaults, Redis, CosmosDB, EventHubs, Grafana Dashboards e Datasources. Filtrável por tipo.</li>
        <li><strong>System:</strong> Seu projeto. Agrupa todos os Components e Resources, com um grafo de dependências e visão de relações.</li>
      </ul>
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText }}>
      Mude o dropdown de Kind na barra lateral esquerda para ver diferentes tipos de entidade.
    </Typography>
  </Box>,

  /* 8 — Filtros e busca */
  <Box key="pt-8">
    <Typography variant="body2" paragraph>
      O Catálogo oferece duas formas de refinar o que você está vendo:
    </Typography>
    <Typography variant="body2" component="div">
      <ul>
        <li><strong>Filtros da barra lateral:</strong> filtre por Owner, Lifecycle e itens pessoais (Owned ou Starred).</li>
        <li><strong>Busca por palavra-chave:</strong> o campo no canto superior direito filtra a visão atual em todas as colunas visíveis (nome, proprietário, tipo, descrição).</li>
      </ul>
    </Typography>
    <Typography variant="body2" paragraph>
      Exemplo: para encontrar um KeyVault específico, defina Kind como "Resource" e depois digite o nome no campo de busca.
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText }}>
      Nota: esta busca filtra apenas a visão do Kind atual. A barra de Busca global no topo da página busca tudo no Backstage.
    </Typography>
  </Box>,

  /* 9 — Encontre seu sistema */
  <Box key="pt-9">
    <Typography variant="body2" paragraph>
      Seu projeto é organizado como um System: o agrupamento de nível superior
      que mantém todos os microsserviços (Components) e infraestrutura
      (Resources) do seu time sob o mesmo teto.
    </Typography>
    <Typography variant="body2" paragraph>
      Para encontrá-lo: vá ao Catálogo, defina Kind como "System", localize seu
      projeto pelo nome ou use o campo de busca, e clique para abrir.
    </Typography>
    <Typography variant="body2" paragraph>
      A página do System é a base do seu projeto no IDP. Daqui você pode ver
      tudo que pertence ao seu projeto, entender como as peças se conectam e
      navegar para qualquer Component ou Resource específico.
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText }}>
      Dica: favorite seu System para acesso com um clique a partir da Página Inicial.
    </Typography>
  </Box>,

  /* 10 — Visão do sistema */
  <Box key="pt-10">
    <OnboardingImage imagePath="images/Journey%201/Phase%203%20-%20Step%202/Frame.png" alt="Visão do sistema" />
    <Typography variant="body2" paragraph>
      Esta é a base do seu projeto. O cartão About à esquerda mostra metadados importantes:
    </Typography>
    <Typography variant="body2" component="div">
      <ul>
        <li><strong>Description:</strong> o que este sistema faz</li>
        <li><strong>Owner:</strong> qual time é responsável</li>
        <li><strong>Domain:</strong> a área de negócio a que pertence</li>
        <li><strong>View Source / View TechDocs:</strong> links rápidos para o repositório GitHub e documentação publicada</li>
      </ul>
    </Typography>
    <Typography variant="body2">
      Abaixo, você encontrará listas de todos os Components (suas aplicações) e
      Resources (infraestrutura) pertencentes a este System. Clique em qualquer
      nome para navegar até sua página de detalhes com informações específicas e
      Plugin Tabs.
    </Typography>
  </Box>,

  /* 11 — Grafo de relações */
  <Box key="pt-11">
    <OnboardingImage imagePath="images/Journey%201/Phase%203%20-%20Step%203/Frame.png" alt="Grafo de relações" />
    <Typography variant="body2" paragraph>
      No lado direito da página do System, o cartão Relations mostra uma prévia
      de como tudo no seu projeto se conecta: quais Components dependem de quais
      Resources e como os serviços se relacionam entre si.
    </Typography>
    <Typography variant="body2" paragraph>
      Clique em "View graph" na parte inferior para uma visão interativa em tela
      cheia onde você pode dar zoom, arrastar, clicar em nós individuais e
      aplicar filtros para focar em partes específicas.
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText }}>
      Especialmente útil quando você é novo no projeto, solucionando problemas
      em múltiplos serviços ou avaliando o impacto de uma mudança.
    </Typography>
  </Box>,

  /* 12 — ArgoCD: seu painel de deploy */
  <Box key="pt-12">
    <OnboardingVideo videoPath="Journey%201/Step13/Video_Onboarding_Journey1_Step13_PT.mp4" subtitlePath="Journey%201/Step13/Legenda/Step%2013%20PT.srt" />
    <Typography variant="body2" paragraph>
      O ArgoCD gerencia todos os deployments. Ele monitora seu repositório GitOps
      e aplica automaticamente as mudanças no cluster Kubernetes. Você o usará
      para verificar se os deploys foram bem-sucedidos e monitorar a saúde da aplicação.
    </Typography>
    <Box style={{ backgroundColor: c.infoBoxBg, border: `1px solid ${c.infoBoxBorder}`, borderRadius: 4, padding: '10px 16px', textAlign: 'center', marginBottom: 12 }}>
      <Typography variant="body2">
        Acesso:{' '}
        <a href="https://argocd-nlar.vsa.nestle.biz" target="_blank" rel="noopener noreferrer" style={{ color: c.primary }}>
          argocd-nlar.vsa.nestle.biz
        </a>
      </Typography>
    </Box>
    <Typography variant="body2" paragraph>Use Label Filters para encontrar suas aplicações:</Typography>
    <Typography variant="body2" component="div">
      <ul>
        <li><strong>env=dv</strong> para <strong>Development</strong></li>
        <li><strong>env=ts</strong> para <strong>Test</strong></li>
        <li><strong>env=pr</strong> para <strong>Production</strong></li>
      </ul>
    </Typography>
    <Typography variant="body2" paragraph>Você também pode filtrar por domínio ou nome do sistema.</Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText }}>
      Todas as aplicações têm Auto Sync, Auto Prune e Self Heal habilitados. Se não aparecer nada ao abrir o ArgoCD, solicite acesso via ticket.
    </Typography>
  </Box>,

  /* 13 — Grafana: seus logs */
  <Box key="pt-13">
    <OnboardingVideo videoPath="Journey%201/Step14/Video_Onboarding_Journey1_Step14_PT.mp4" subtitlePath="Journey%201/Step14/Legenda/step14_grafana_PT.srt" />
    <Typography variant="body2" paragraph>
      Os logs das aplicações são agregados via Grafana Loki e disponibilizados pelo Grafana. Cada ambiente tem sua própria instância dedicada:
    </Typography>
    <Box style={{ backgroundColor: c.infoBoxBg, border: `1px solid ${c.infoBoxBorder}`, borderRadius: 4, padding: '10px 16px', textAlign: 'center', marginBottom: 12 }}>
      <Typography variant="body2">
        Development:{' '}
        <a href="https://grafana-dv.vsa.nestle.biz" target="_blank" rel="noopener noreferrer" style={{ color: c.primary }}>grafana-dv.vsa.nestle.biz</a>
      </Typography>
      <Typography variant="body2">
        Test/Homolog:{' '}
        <a href="https://grafana-ts.vsa.nestle.biz" target="_blank" rel="noopener noreferrer" style={{ color: c.primary }}>grafana-ts.vsa.nestle.biz</a>
      </Typography>
      <Typography variant="body2">
        Production:{' '}
        <a href="https://grafana.vsa.nestle.biz" target="_blank" rel="noopener noreferrer" style={{ color: c.primary }}>grafana.vsa.nestle.biz</a>
      </Typography>
    </Box>
    <Typography variant="body2" paragraph>Como consultar logs</Typography>
    <Typography variant="body2" component="div">
      <ul>
        <li><strong>1.</strong> Vá para Explore na barra lateral esquerda</li>
        <li><strong>2.</strong> Selecione o datasource Loki (canto superior esquerdo)</li>
        <li><strong>3.</strong> Escolha o modo Builder (visual) ou Code</li>
        <li><strong>4.</strong> Clique em Run Query</li>
      </ul>
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText, marginBottom: 8 }}>
      Cada time só tem acesso à sua própria Organization. Verifique o canto superior esquerdo para ver ou trocar de Organization.
    </Typography>
  </Box>,

  /* 14 — GitHub e Azure DevOps */
  <Box key="pt-14">
    <OnboardingVideo videoPath="Journey%201/Step15/Video_Onboarding_Journey1_Step15_PT.mp4" subtitlePath="Journey%201/Step15/Legenda/step15_github_cicd_PT.srt" />
    <Typography variant="body2" style={{ fontWeight: 700, marginBottom: 8 }}>GitHub</Typography>
    <Typography variant="body2" paragraph>
      Cada projeto tem dois repositórios: o repositório da Aplicação (seu código-fonte) e o repositório GitOps (configuração de deployment, com pastas para cada ambiente: dv, ts, pr). Você envia código para o repositório da Aplicação. O repositório GitOps é atualizado automaticamente pela pipeline. Nunca crie branches em repositórios GitOps, eles devem usar apenas main.
    </Typography>
    <Typography variant="body2" style={{ fontWeight: 700, marginBottom: 8 }}>Azure DevOps</Typography>
    <Typography variant="body2" paragraph>
      Executa suas pipelines CI/CD. Cada pipeline tem 3 estágios:
    </Typography>
    <Typography variant="body2" component="div">
      <ul>
        <li><strong>1. Testes:</strong> testes unitários + análise de qualidade SonarQube</li>
        <li><strong>2. Build e Push de Imagem:</strong> imagem Docker para o Azure Container Registry</li>
        <li><strong>3. Atualizar versão no GitOps:</strong> atualiza o bom.yaml com a nova versão da imagem</li>
      </ul>
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText, marginBottom: 8 }}>
      Todas as pipelines são pré-configuradas pelo Time de Plataforma. Cada time tem seu próprio workspace no Azure DevOps.
    </Typography>
    <Box style={{ backgroundColor: c.warningBannerBg, border: `1px solid ${c.warningBoxBorder}`, borderRadius: 4, padding: '12px 16px', marginTop: 8 }}>
      <Typography variant="body2">
        Dockerfiles são criados e mantidos exclusivamente pelo Time de Plataforma. Mesmo se você criar o seu próprio, a pipeline não o usará.
      </Typography>
    </Box>
  </Box>,

  /* 15 — Suas permissões como desenvolvedor */
  <Box key="pt-15">
    <Typography variant="body2" paragraph>
      O IDP separa o que Desenvolvedores e Tech Leads podem fazer. Entender suas permissões evita confusão e economiza tempo.
    </Typography>
    <Box style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
      <Box style={{ flex: 1, border: `2px solid ${c.success}`, borderRadius: 8, padding: '12px 14px' }}>
        <Typography variant="body2" style={{ fontWeight: 700, color: c.success, marginBottom: 8 }}>Você PODE</Typography>
        <CanItem text="Navegar no Catálogo inteiro" />
        <CanItem text="Acessar ArgoCD para status de deploy" />
        <CanItem text="Acessar Grafana para logs" />
        <CanItem text="Acessar Azure DevOps para pipelines" />
        <CanItem text="Acessar GitHub para enviar código" />
        <CanItem text="Ver Plugin Tabs nos Components" />
      </Box>
      <Box style={{ flex: 1, border: `2px solid ${c.error}`, borderRadius: 8, padding: '12px 14px' }}>
        <Typography variant="body2" style={{ fontWeight: 700, color: c.error, marginBottom: 8 }}>Você NÃO PODE</Typography>
        <CannotItem text="Criar ou editar Resources" />
        <CannotItem text="Criar aplicações a partir de templates" />
        <CannotItem text="Promover ambientes" />
        <CannotItem text="Gerenciar secrets no KeyVault" />
        <CannotItem text="Gerar tokens SAS" />
        <CannotItem text="Gerenciar App Configurations" />
      </Box>
    </Box>
    <Typography variant="body2">
      Precisa que algo seja criado ou alterado? Entre em contato com seu Tech Lead. Eles têm permissões completas de criação e edição.
    </Typography>
  </Box>,

  /* 16 — Regras de segurança (obrigatório) */
  <Box key="pt-16">
    <Typography variant="body2" paragraph>
      Estas regras são obrigatórias. O não cumprimento aciona alertas automatizados e pode resultar em revogação de acesso.
    </Typography>
    <Box style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
      <Box style={{ flex: 1, border: `2px solid ${c.error}`, borderRadius: 8, padding: '12px 14px' }}>
        <Typography variant="body2" style={{ fontWeight: 700, color: c.error, marginBottom: 8 }}>Proibido</Typography>
        <CannotItem text="Acesso ao Azure Portal" />
        <CannotItem text="Secrets hardcoded no código" />
        <CannotItem text="Código fora do Azure DevOps/GitHub" />
        <CannotItem text="Recursos de nuvem externos" />
        <CannotItem text="ChatGPT público com dados da Nestlé" />
      </Box>
      <Box style={{ flex: 1, border: `2px solid ${c.success}`, borderRadius: 8, padding: '12px 14px' }}>
        <Typography variant="body2" style={{ fontWeight: 700, color: c.success, marginBottom: 8 }}>Obrigatório</Typography>
        <CanItem text="VPN ou Citrix sempre" />
        <CanItem text="Cobertura de testes acima de 95%" />
        <CanItem text="Code review por pares em todos os PRs" />
        <CanItem text="Pipeline CI sempre passando" />
        <CanItem text="Arquivos apenas em OneDrive/Confluence/Wiki" />
      </Box>
    </Box>
    <Typography variant="body2">
      Estas regras protegem os dados da Nestlé, garantem conformidade com políticas
      globais de segurança e mantêm a integridade da plataforma. Elas se aplicam
      a todos os membros do time, independente do papel.
    </Typography>
  </Box>,

  /* 17 — Jornada 1 completa! */
  <Box key="pt-17" style={{ textAlign: 'center' }}>
    <CheckCircle style={{ fontSize: 56, color: c.success, marginBottom: 8 }} />
    <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 4 }}>
      Jornada 1 completa!
    </Typography>
    <Typography variant="body2" style={{ color: c.subtitleText, marginBottom: 16 }}>
      Parabéns! Agora você tem a base para se mover com confiança pela plataforma.
    </Typography>
    <Box style={{ textAlign: 'left', marginBottom: 16 }}>
      <Typography variant="body2" style={{ fontWeight: 700, marginBottom: 8 }}>O que você aprendeu</Typography>
      <CanItem text="Como navegar no portal Backstage: Home, Busca, Catálogo" />
      <CanItem text="Como encontrar seu projeto: Systems, Components, Resources, Grafo de Relações" />
      <CanItem text="As ferramentas que você usará diariamente: ArgoCD, Grafana, GitHub, Azure DevOps" />
      <CanItem text="Suas permissões como Desenvolvedor e as regras de segurança obrigatórias" />
    </Box>
    <Box style={{ textAlign: 'left' }}>
      <Typography variant="body2" style={{ fontWeight: 700, marginBottom: 8 }}>Próximo passo</Typography>
      <Typography variant="body2">
        Complete o quiz para validar seu conhecimento. Você precisa de pelo menos
        90% (9 de 10 corretas) para passar. Se não passar na primeira tentativa,
        você pode refazer. Quando passar, a Jornada 2: "Deploy: Do Código ao Ar"
        será desbloqueada.
      </Typography>
    </Box>
  </Box>,
];

/* ================================================================
 *  Journey 0 — Spanish (es) content
 * ================================================================ */

const getJourney0ContentES = (c: OnboardingColors): React.ReactNode[] => [
  /* 0 — Bienvenido al IDP */
  <Box key="es-0">
    <Typography variant="body2" paragraph>
      El IDP es el portal centralizado de desarrolladores de Nestlé, construido
      con Backstage. En lugar de saltar entre herramientas, tienes un único
      punto de entrada para tus servicios, infraestructura, documentación,
      pipelines y monitoreo.
    </Typography>
    <Typography variant="body2" component="div">
      <strong>Lo que necesitas saber</strong>
      <ul>
        <li>Tu proyecto está organizado como un System, agrupando tus aplicaciones (Components) e infraestructura (Resources).</li>
        <li>El código está en GitHub. Los despliegues van a Kubernetes vía ArgoCD, un clúster por entorno.</li>
        <li>Cada despliegue requiere archivos GitOps como fuente de verdad, en un repositorio unificado en GitHub.</li>
        <li>Los recursos corren en Azure Cloud. Los logs están en Grafana, una instancia por entorno.</li>
        <li>Todo está catalogado en Backstage — servicios, recursos, DNS y documentación.</li>
      </ul>
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Lo que aprenderás en esta jornada</strong>
    </Typography>
    <Typography variant="body2" paragraph>
      Cómo navegar en Backstage, encontrar tu System, usar ArgoCD, Grafana,
      GitHub y Azure DevOps, y entender tus permisos y reglas de seguridad.
    </Typography>
  </Box>,

  /* 1 — Las herramientas a tu alrededor */
  <Box key="es-1">
    <Typography variant="body2" paragraph>
      La plataforma integra varias herramientas, cada una encargándose de una
      parte específica de tu flujo de trabajo. Backstage es tu hub central —
      empiezas aquí y navegas a las demás según sea necesario.
    </Typography>
    <OnboardingVideo videoPath="Journey%201/Step2/Video_Onboarding_Journey1_Step2_ES.mp4" subtitlePath="Journey%201/Step2/Legendas/step02_tools_around_you_ES.srt" />
    <Typography variant="body2" component="div">
      <strong>Tu kit de herramientas diario</strong>
      <ul>
        <li><strong>Backstage:</strong> Portal del Desarrollador. Navega el catálogo, encuentra componentes y recursos, accede a documentación y herramientas externas.</li>
        <li><strong>GitHub:</strong> Repositorios de código fuente y GitOps. Envía código y crea pull requests aquí.</li>
        <li><strong>Azure DevOps:</strong> Pipelines CI/CD. Ejecuta pruebas automáticamente, construye imágenes y actualiza GitOps en cada push.</li>
        <li><strong>ArgoCD:</strong> Despliegues. Monitorea GitOps y aplica cambios en Kubernetes automáticamente.</li>
        <li><strong>Grafana + Loki:</strong> Logs y monitoreo. Consulta logs de aplicaciones en todos los entornos vía Explore.</li>
      </ul>
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText }}>
      Crossplane y Kubernetes trabajan entre bastidores, no interactúas con ellos directamente.
    </Typography>
  </Box>,

  /* 2 — Cómo se conecta todo */
  <Box key="es-2">
    <OnboardingImage imagePath="images/Journey%201/Phase%201%20-%20Step%203/Frame.png" alt="Cómo se conecta todo" height={180} />
    <Typography variant="body2" paragraph>
      Este es el flujo completo cada vez que envías código:
    </Typography>
    <Typography variant="body2" component="div">
      <ol>
        <li>Envías código a GitHub. Un pull request activa el pipeline CI.</li>
        <li>El pipeline ejecuta tres etapas: Pruebas (unitarias + SonarQube) → Build y Push de Imagen (Docker → ACR) → Actualizar versión en GitOps (bom.yaml).</li>
        <li>ArgoCD detecta el cambio en GitOps y despliega automáticamente en Kubernetes.</li>
        <li>Verificas el estado del despliegue en ArgoCD y los logs de la aplicación en Grafana.</li>
      </ol>
    </Typography>
    <Box style={{ backgroundColor: c.warningBannerBg, border: `1px solid ${c.warningBoxBorder}`, borderRadius: 4, padding: '12px 16px', marginTop: 8 }}>
      <Typography variant="body2">
        El Dockerfile NO es tu responsabilidad, el Equipo de Plataforma lo crea y mantiene. Incluso si creas uno, no se usará.
      </Typography>
    </Box>
  </Box>,

  /* 3 — La página de inicio */
  <Box key="es-3">
    <Typography variant="body2" paragraph>
      La Página de Inicio es tu punto de partida cada vez que abres Backstage.
      Se adapta a tu uso y te da acceso rápido a lo que más importa:
    </Typography>
    <Typography variant="body2" component="div">
      <ul>
        <li><strong>Más Visitados:</strong> elementos que accedes con mayor frecuencia</li>
        <li><strong>Visitados Recientemente:</strong> historial cronológico de lo que has visto</li>
        <li><strong>Entidades Favoritas:</strong> elementos que marcaste manualmente como favoritos</li>
        <li><strong>Documentación Destacada:</strong> documentación publicada por TechDocs</li>
      </ul>
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText }}>
      Consejo: marca como favorito la página del System de tu proyecto desde el inicio, aparecerá en Entidades Favoritas para acceso con un clic.
    </Typography>
  </Box>,

  /* 4 — Acciones rápidas */
  <Box key="es-4">
    <Typography variant="body2" paragraph>
      Estos botones son atajos a las tareas más comunes en Backstage:
    </Typography>
    <Typography variant="body2" component="div">
      <ul>
        <li><strong>CREATE COMPONENT:</strong> Visible para todos, pero al hacer clic como Desarrollador mostrará una página de error. Esto es esperado, no es un bug.</li>
        <li><strong>BROWSE CATALOG:</strong> Abre la página del Catálogo donde exploras Components, Resources y Systems.</li>
        <li><strong>VIEW DOCS:</strong> Abre TechDocs con guías de la plataforma y material de referencia publicado por el equipo.</li>
      </ul>
    </Typography>
    <Box style={{ backgroundColor: c.warningBannerBg, border: `1px solid ${c.warningBoxBorder}`, borderRadius: 4, padding: '12px 16px', marginTop: 8 }}>
      <Typography variant="body2">
        Los Desarrolladores tienen acceso de solo lectura. Solo los Tech Leads pueden crear componentes y recursos. Si necesitas algo creado, pídelo a tu Tech Lead.
      </Typography>
    </Box>
  </Box>,

  /* 5 — La barra de búsqueda */
  <Box key="es-5">
    <Typography variant="body2" paragraph>
      La barra de búsqueda en la parte superior de cada página es la forma más
      rápida de encontrar cualquier cosa en el portal. Busca en todas las
      entidades — Components, Resources, Systems, APIs y documentación —
      indexando todos los campos: nombre, tipo, propietario y descripción.
    </Typography>
    <Typography variant="body2" paragraph>
      Los resultados aparecen mientras escribes. Haz clic en cualquier resultado para ir directamente a su página de detalle.
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText }}>
      Usa la Búsqueda cuando sepas lo que estás buscando. Usa el Catálogo cuando quieras navegar por categoría con filtros.
    </Typography>
  </Box>,

  /* 6 — Abrir el catálogo */
  <Box key="es-6">
    <Typography variant="body2" paragraph>
      El Catálogo es el corazón de Backstage. Todas las entidades de la
      plataforma están listadas y organizadas aquí: cada Component, Resource,
      System, API y más. Piensa en él como el directorio maestro de todo con lo
      que tu equipo trabaja.
    </Typography>
    <Typography variant="body2" paragraph>
      Puedes acceder haciendo clic en "Catalog" en la barra lateral izquierda o en el botón "BROWSE CATALOG" en la Página de Inicio.
    </Typography>
    <Typography variant="body2">
      Se abre mostrando Components por defecto. La barra lateral izquierda tiene filtros (Owner, Lifecycle) y en la esquina superior derecha hay un campo de búsqueda por palabras clave.
    </Typography>
  </Box>,

  /* 7 — Entendiendo los tipos */
  <Box key="es-7">
    <OnboardingVideo videoPath="Journey%201/Step8/Video_Onboarding_Journey1_Step8_ES.mp4" subtitlePath="Journey%201/Step8/Legenda/step08_understanding_kinds_ES.srt" />
    <Typography variant="body2" paragraph>
      Los Kinds (tipos) son la forma en que el Catálogo categoriza todo. Hay tres que usarás a diario:
    </Typography>
    <Typography variant="body2" component="div">
      <ul>
        <li><strong>Component:</strong> Tus aplicaciones: microservicios, webapps, bibliotecas, frontends. Cada uno tiene Plugin Tabs para Azure PRs, CI/CD, Grafana y SonarQube.</li>
        <li><strong>Resource:</strong> Elementos de infraestructura: Storage Accounts, KeyVaults, Redis, CosmosDB, EventHubs, Grafana Dashboards y Datasources. Filtrable por tipo.</li>
        <li><strong>System:</strong> Tu proyecto. Agrupa todos los Components y Resources, con un grafo de dependencias y vista de relaciones.</li>
      </ul>
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText }}>
      Cambia el dropdown de Kind en la barra lateral izquierda para ver diferentes tipos de entidad.
    </Typography>
  </Box>,

  /* 8 — Filtros y búsqueda */
  <Box key="es-8">
    <Typography variant="body2" paragraph>
      El Catálogo te ofrece dos formas de refinar lo que estás viendo:
    </Typography>
    <Typography variant="body2" component="div">
      <ul>
        <li><strong>Filtros de la barra lateral:</strong> filtra por Owner, Lifecycle y elementos personales (Owned o Starred).</li>
        <li><strong>Búsqueda por palabras clave:</strong> el campo en la esquina superior derecha filtra la vista actual en todas las columnas visibles (nombre, propietario, tipo, descripción).</li>
      </ul>
    </Typography>
    <Typography variant="body2" paragraph>
      Ejemplo: para encontrar un KeyVault específico, establece Kind como "Resource" y luego escribe el nombre en el campo de búsqueda.
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText }}>
      Nota: esta búsqueda solo filtra la vista del Kind actual. La barra de Búsqueda global en la parte superior de la página busca todo en Backstage.
    </Typography>
  </Box>,

  /* 9 — Encuentra tu sistema */
  <Box key="es-9">
    <Typography variant="body2" paragraph>
      Tu proyecto está organizado como un System: la agrupación de nivel
      superior que mantiene todos los microservicios (Components) e
      infraestructura (Resources) de tu equipo bajo un mismo techo.
    </Typography>
    <Typography variant="body2" paragraph>
      Para encontrarlo: ve al Catálogo, establece Kind como "System", localiza
      tu proyecto por nombre o usa el campo de búsqueda, y haz clic para abrir.
    </Typography>
    <Typography variant="body2" paragraph>
      La página del System es la base de tu proyecto en el IDP. Desde aquí
      puedes ver todo lo que pertenece a tu proyecto, entender cómo se conectan
      las piezas y navegar a cualquier Component o Resource específico.
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText }}>
      Consejo: marca tu System como favorito para acceso con un clic desde la Página de Inicio.
    </Typography>
  </Box>,

  /* 10 — Vista del sistema */
  <Box key="es-10">
    <OnboardingImage imagePath="images/Journey%201/Phase%203%20-%20Step%202/Frame.png" alt="Vista del sistema" />
    <Typography variant="body2" paragraph>
      Esta es la base de tu proyecto. La tarjeta About a la izquierda muestra metadatos clave:
    </Typography>
    <Typography variant="body2" component="div">
      <ul>
        <li><strong>Description:</strong> qué hace este sistema</li>
        <li><strong>Owner:</strong> qué equipo es responsable</li>
        <li><strong>Domain:</strong> el área de negocio al que pertenece</li>
        <li><strong>View Source / View TechDocs:</strong> enlaces rápidos al repositorio GitHub y documentación publicada</li>
      </ul>
    </Typography>
    <Typography variant="body2">
      Debajo, encontrarás listas de todos los Components (tus aplicaciones) y
      Resources (infraestructura) pertenecientes a este System. Haz clic en
      cualquier nombre para navegar a su página de detalle con información
      específica y Plugin Tabs.
    </Typography>
  </Box>,

  /* 11 — Grafo de relaciones */
  <Box key="es-11">
    <OnboardingImage imagePath="images/Journey%201/Phase%203%20-%20Step%203/Frame.png" alt="Grafo de relaciones" />
    <Typography variant="body2" paragraph>
      En el lado derecho de la página del System, la tarjeta Relations muestra
      una vista previa de cómo todo en tu proyecto se conecta: qué Components
      dependen de qué Resources y cómo los servicios se relacionan entre sí.
    </Typography>
    <Typography variant="body2" paragraph>
      Haz clic en "View graph" en la parte inferior para una vista interactiva
      a pantalla completa donde puedes hacer zoom, arrastrar, hacer clic en
      nodos individuales y aplicar filtros para enfocarte en partes específicas.
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText }}>
      Especialmente útil cuando eres nuevo en un proyecto, solucionando
      problemas en múltiples servicios o evaluando el impacto de un cambio.
    </Typography>
  </Box>,

  /* 12 — ArgoCD: tu panel de despliegue */
  <Box key="es-12">
    <OnboardingVideo videoPath="Journey%201/Step13/Video_Onboarding_Journey1_Step13_ES.mp4" subtitlePath="Journey%201/Step13/Legenda/step13_argocd_ES.srt" />
    <Typography variant="body2" paragraph>
      ArgoCD gestiona todos los despliegues. Monitorea tu repositorio GitOps y
      aplica automáticamente los cambios al clúster Kubernetes. Lo usarás para
      verificar si los despliegues fueron exitosos y monitorear la salud de la
      aplicación.
    </Typography>
    <Box style={{ backgroundColor: c.infoBoxBg, border: `1px solid ${c.infoBoxBorder}`, borderRadius: 4, padding: '10px 16px', textAlign: 'center', marginBottom: 12 }}>
      <Typography variant="body2">
        Acceso:{' '}
        <a href="https://argocd-nlar.vsa.nestle.biz" target="_blank" rel="noopener noreferrer" style={{ color: c.primary }}>
          argocd-nlar.vsa.nestle.biz
        </a>
      </Typography>
    </Box>
    <Typography variant="body2" paragraph>Usa Label Filters para encontrar tus aplicaciones:</Typography>
    <Typography variant="body2" component="div">
      <ul>
        <li><strong>env=dv</strong> para <strong>Development</strong></li>
        <li><strong>env=ts</strong> para <strong>Test</strong></li>
        <li><strong>env=pr</strong> para <strong>Production</strong></li>
      </ul>
    </Typography>
    <Typography variant="body2" paragraph>También puedes filtrar por dominio o nombre del sistema.</Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText }}>
      Todas las aplicaciones tienen Auto Sync, Auto Prune y Self Heal habilitados. Si no ves nada al abrir ArgoCD, solicita acceso mediante ticket.
    </Typography>
  </Box>,

  /* 13 — Grafana: tus logs */
  <Box key="es-13">
    <OnboardingVideo videoPath="Journey%201/Step14/Video_Onboarding_Journey1_Step14_ES.mp4" subtitlePath="Journey%201/Step14/Legenda/step14_grafana_ES.srt" />
    <Typography variant="body2" paragraph>
      Los logs de las aplicaciones se agregan vía Grafana Loki y están disponibles a través de Grafana. Cada entorno tiene su propia instancia dedicada:
    </Typography>
    <Box style={{ backgroundColor: c.infoBoxBg, border: `1px solid ${c.infoBoxBorder}`, borderRadius: 4, padding: '10px 16px', textAlign: 'center', marginBottom: 12 }}>
      <Typography variant="body2">
        Development:{' '}
        <a href="https://grafana-dv.vsa.nestle.biz" target="_blank" rel="noopener noreferrer" style={{ color: c.primary }}>grafana-dv.vsa.nestle.biz</a>
      </Typography>
      <Typography variant="body2">
        Test/Homolog:{' '}
        <a href="https://grafana-ts.vsa.nestle.biz" target="_blank" rel="noopener noreferrer" style={{ color: c.primary }}>grafana-ts.vsa.nestle.biz</a>
      </Typography>
      <Typography variant="body2">
        Production:{' '}
        <a href="https://grafana.vsa.nestle.biz" target="_blank" rel="noopener noreferrer" style={{ color: c.primary }}>grafana.vsa.nestle.biz</a>
      </Typography>
    </Box>
    <Typography variant="body2" paragraph>Cómo consultar logs</Typography>
    <Typography variant="body2" component="div">
      <ul>
        <li><strong>1.</strong> Ve a Explore en la barra lateral izquierda</li>
        <li><strong>2.</strong> Selecciona el datasource Loki (esquina superior izquierda)</li>
        <li><strong>3.</strong> Elige el modo Builder (visual) o Code</li>
        <li><strong>4.</strong> Haz clic en Run Query</li>
      </ul>
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText, marginBottom: 8 }}>
      Cada equipo solo tiene acceso a su propia Organization. Verifica la esquina superior izquierda para ver o cambiar de Organization.
    </Typography>
  </Box>,

  /* 14 — GitHub y Azure DevOps */
  <Box key="es-14">
    <OnboardingVideo videoPath="Journey%201/Step15/Video_Onboarding_Journey1_Step15_ES.mp4" subtitlePath="Journey%201/Step15/Legenda/step15_github_cicd_ES.srt" />
    <Typography variant="body2" style={{ fontWeight: 700, marginBottom: 8 }}>GitHub</Typography>
    <Typography variant="body2" paragraph>
      Cada proyecto tiene dos repositorios: el repositorio de la Aplicación (tu código fuente) y el repositorio GitOps (configuración de despliegue, con carpetas para cada entorno: dv, ts, pr). Envías código al repositorio de la Aplicación. El repositorio GitOps se actualiza automáticamente por el pipeline. Nunca crees branches en repositorios GitOps, deben usar solo main.
    </Typography>
    <Typography variant="body2" style={{ fontWeight: 700, marginBottom: 8 }}>Azure DevOps</Typography>
    <Typography variant="body2" paragraph>
      Ejecuta tus pipelines CI/CD. Cada pipeline tiene 3 etapas:
    </Typography>
    <Typography variant="body2" component="div">
      <ul>
        <li><strong>1. Pruebas:</strong> pruebas unitarias + análisis de calidad SonarQube</li>
        <li><strong>2. Build y Push de Imagen:</strong> imagen Docker al Azure Container Registry</li>
        <li><strong>3. Actualizar versión en GitOps:</strong> actualiza bom.yaml con la nueva versión de la imagen</li>
      </ul>
    </Typography>
    <Typography variant="body2" style={{ fontStyle: 'italic', color: c.subtitleText, marginBottom: 8 }}>
      Todos los pipelines están preconfigurados por el Equipo de Plataforma. Cada equipo tiene su propio workspace en Azure DevOps.
    </Typography>
    <Box style={{ backgroundColor: c.warningBannerBg, border: `1px solid ${c.warningBoxBorder}`, borderRadius: 4, padding: '12px 16px', marginTop: 8 }}>
      <Typography variant="body2">
        Los Dockerfiles son creados y mantenidos exclusivamente por el Equipo de Plataforma. Incluso si creas el tuyo propio, el pipeline no lo usará.
      </Typography>
    </Box>
  </Box>,

  /* 15 — Tus permisos como desarrollador */
  <Box key="es-15">
    <Typography variant="body2" paragraph>
      El IDP separa lo que los Desarrolladores y Tech Leads pueden hacer. Entender tus permisos evita confusión y ahorra tiempo.
    </Typography>
    <Box style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
      <Box style={{ flex: 1, border: `2px solid ${c.success}`, borderRadius: 8, padding: '12px 14px' }}>
        <Typography variant="body2" style={{ fontWeight: 700, color: c.success, marginBottom: 8 }}>Tú PUEDES</Typography>
        <CanItem text="Navegar el Catálogo completo" />
        <CanItem text="Acceder a ArgoCD para estado de despliegue" />
        <CanItem text="Acceder a Grafana para logs" />
        <CanItem text="Acceder a Azure DevOps para pipelines" />
        <CanItem text="Acceder a GitHub para enviar código" />
        <CanItem text="Ver Plugin Tabs en Components" />
      </Box>
      <Box style={{ flex: 1, border: `2px solid ${c.error}`, borderRadius: 8, padding: '12px 14px' }}>
        <Typography variant="body2" style={{ fontWeight: 700, color: c.error, marginBottom: 8 }}>Tú NO PUEDES</Typography>
        <CannotItem text="Crear o editar Resources" />
        <CannotItem text="Crear aplicaciones a partir de templates" />
        <CannotItem text="Promover entornos" />
        <CannotItem text="Gestionar secrets en KeyVault" />
        <CannotItem text="Generar tokens SAS" />
        <CannotItem text="Gestionar App Configurations" />
      </Box>
    </Box>
    <Typography variant="body2">
      ¿Necesitas que algo sea creado o cambiado? Contacta a tu Tech Lead. Ellos tienen permisos completos de creación y edición.
    </Typography>
  </Box>,

  /* 16 — Reglas de seguridad (obligatorio) */
  <Box key="es-16">
    <Typography variant="body2" paragraph>
      Estas reglas son obligatorias. El incumplimiento activa alertas automatizadas y puede resultar en la revocación del acceso.
    </Typography>
    <Box style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
      <Box style={{ flex: 1, border: `2px solid ${c.error}`, borderRadius: 8, padding: '12px 14px' }}>
        <Typography variant="body2" style={{ fontWeight: 700, color: c.error, marginBottom: 8 }}>Prohibido</Typography>
        <CannotItem text="Acceso al Azure Portal" />
        <CannotItem text="Secrets hardcoded en el código" />
        <CannotItem text="Código fuera de Azure DevOps/GitHub" />
        <CannotItem text="Recursos de nube externos" />
        <CannotItem text="ChatGPT público con datos de Nestlé" />
      </Box>
      <Box style={{ flex: 1, border: `2px solid ${c.success}`, borderRadius: 8, padding: '12px 14px' }}>
        <Typography variant="body2" style={{ fontWeight: 700, color: c.success, marginBottom: 8 }}>Obligatorio</Typography>
        <CanItem text="VPN o Citrix siempre" />
        <CanItem text="Cobertura de pruebas superior al 95%" />
        <CanItem text="Revisión de código por pares en todos los PRs" />
        <CanItem text="Pipeline CI siempre en verde" />
        <CanItem text="Archivos solo en OneDrive/Confluence/Wiki" />
      </Box>
    </Box>
    <Typography variant="body2">
      Estas reglas protegen los datos de Nestlé, aseguran el cumplimiento de
      políticas globales de seguridad y mantienen la integridad de la plataforma.
      Se aplican a todos los miembros del equipo independientemente del rol.
    </Typography>
  </Box>,

  /* 17 — ¡Jornada 1 completa! */
  <Box key="es-17" style={{ textAlign: 'center' }}>
    <CheckCircle style={{ fontSize: 56, color: c.success, marginBottom: 8 }} />
    <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 4 }}>
      ¡Jornada 1 completa!
    </Typography>
    <Typography variant="body2" style={{ color: c.subtitleText, marginBottom: 16 }}>
      ¡Felicidades! Ahora tienes la base para moverte con confianza por la plataforma.
    </Typography>
    <Box style={{ textAlign: 'left', marginBottom: 16 }}>
      <Typography variant="body2" style={{ fontWeight: 700, marginBottom: 8 }}>Lo que has aprendido</Typography>
      <CanItem text="Cómo navegar el portal Backstage: Home, Búsqueda, Catálogo" />
      <CanItem text="Cómo encontrar tu proyecto: Systems, Components, Resources, Grafo de Relaciones" />
      <CanItem text="Las herramientas que usarás a diario: ArgoCD, Grafana, GitHub, Azure DevOps" />
      <CanItem text="Tus permisos como Desarrollador y las reglas de seguridad obligatorias" />
    </Box>
    <Box style={{ textAlign: 'left' }}>
      <Typography variant="body2" style={{ fontWeight: 700, marginBottom: 8 }}>Siguiente paso</Typography>
      <Typography variant="body2">
        Completa el quiz para validar tu conocimiento. Necesitas al menos 90%
        (9 de 10 correctas) para aprobar. Si no apruebas en el primer intento,
        puedes volver a intentarlo. Cuando apruebes, la Jornada 2: "Deploy: Del
        Código a Producción" se desbloqueará.
      </Typography>
    </Box>
  </Box>,
];
